import { useEffect, useRef } from "preact/hooks";
import { EditorState } from "@codemirror/state";
import { EditorView, keymap, lineNumbers, highlightActiveLine } from "@codemirror/view";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { syntaxHighlighting, defaultHighlightStyle } from "@codemirror/language";
import { markdown } from "@codemirror/lang-markdown";

export type EditorRef = {
  replaceAll: (text: string) => void;
  getValue: () => string;
};

type Props = {
  initial: string;
  onChange: (value: string) => void;
  onReady?: (ref: EditorRef) => void;
};

const lightTheme = EditorView.theme(
  {
    "&": {
      height: "100%",
      fontSize: "14px",
      backgroundColor: "#ffffff",
      color: "#18181b",
    },
    ".cm-scroller": {
      fontFamily:
        'ui-monospace, SFMono-Regular, "JetBrains Mono", Menlo, monospace',
      lineHeight: "1.6",
    },
    ".cm-content": { caretColor: "#18181b" },
    ".cm-cursor, .cm-dropCursor": { borderLeftColor: "#18181b" },
    "&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection":
      { backgroundColor: "#e4e4e7" },
    ".cm-activeLine": { backgroundColor: "#fafafa" },
    ".cm-activeLineGutter": {
      backgroundColor: "#fafafa",
      color: "#18181b",
    },
    ".cm-gutters": {
      backgroundColor: "#ffffff",
      color: "#a1a1aa",
      borderRight: "1px solid #f4f4f5",
    },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 12px 0 8px" },
  },
  { dark: false },
);

export function MarkdownEditor({ initial, onChange, onReady }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  // Capture the latest onChange in a ref so the EditorView created on mount
  // always invokes the freshest callback without re-creating the view.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!hostRef.current) return;
    const state = EditorState.create({
      doc: initial,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        history(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        markdown(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        lightTheme,
        EditorView.lineWrapping,
        EditorView.updateListener.of((u) => {
          if (u.docChanged) onChangeRef.current(u.state.doc.toString());
        }),
      ],
    });
    const view = new EditorView({ state, parent: hostRef.current });
    viewRef.current = view;

    onReady?.({
      replaceAll: (text: string) => {
        const v = viewRef.current;
        if (!v) return;
        v.dispatch({ changes: { from: 0, to: v.state.doc.length, insert: text } });
      },
      getValue: () => viewRef.current?.state.doc.toString() ?? "",
    });

    return () => {
      view.destroy();
      viewRef.current = null;
    };
    // initial is intentionally a one-shot value used to seed the editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={hostRef}
      class="h-full min-h-[60vh] overflow-hidden rounded-md border border-border bg-bg shadow-xs"
    />
  );
}
